// @ts-strict-ignore
import queryString from 'query-string';
import take from 'lodash/take';

import {
  AmountDueInfo,
  BillingFrequency,
  CustomerInfo,
  CustomerType,
  ExperimentalPricingCurve,
  OrgPlanInfo,
  PACKAGE_ID_BY_NAME,
  PricingCurve,
  PricingCurveId,
  PricingCurvePackage,
  PricingCurveVariant,
  UserInfo,
} from 'media/js/pricing/api/types';
import {apiClient} from 'common/util/api-client';
import {handleJsonRequest} from 'common/report/queries/utils';
import {ChangeRequest, PurchaseRequest, QuoteChangeRequest} from 'common/types/schema4api/billing/selfserve/types';
import {objToFormData} from 'common/util';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {BumpBillingData} from 'common/types/app-api/billing_bump/types';

const pricingCurveApiUrl = `/api/app/pricing/curve_info`;
const mtuLevelsApiUrl = `/api/app/pricing/mtu_levels`;
const packagesApiUrl = `/api/app/pricing/packages`;
const planInfoApiUrl = `/api/app/billing/plan_info`;
const selfServeChangeUrl = `/api/app/billing/selfserve_change`;
const selfServePurchaseUrl = `/api/app/billing/selfserve_purchase`;
const selfServeQuoteChangeUrl = `/api/app/billing/selfserve_quote_change`;

function marshalPackageInfo([packageName, pkg]): PricingCurvePackage {
  return {
    id: PACKAGE_ID_BY_NAME[packageName], // keys in packageResults are package name not id
    name: pkg.friendly_name,
    description: pkg.description,
    basePlanPct: parseFloat(pkg.base_plan_percent),
  };
}

export async function getPricingCurve(orgInfo?: OrgPlanInfo): Promise<PricingCurve> {
  /**
   * Retrieve pricing curves for the given org id. For now, we will mimic the existing behavior and send
   * all pricing curves to the frontend (in order to make this an FE-only changeset); later; we can have
   * the API return just the correct pricing curve to use.
   */
  // These URLs are imported from the old API code in media/js/pricing/api;
  const orgId = orgInfo?.orgId;

  const planResults = await apiClient.get(mtuLevelsApiUrl, null, {skipAccessToken: true});
  const packageResults = await apiClient.get(`${packagesApiUrl}?orgId=${orgId}`, null, {skipAccessToken: true});

  // on the other hand, this code is different, however, as we need to reformat the data to fit our new schemae
  const packages: PricingCurvePackage[] = Object.entries<any>(packageResults)
    .map(marshalPackageInfo)
    .filter(({id}) => id); // make sure package exists in id map just in case, for M&E

  return {
    id: PricingCurveId.V2_1_1_GROWTH,
    points: Object.values<any>(planResults[`monthly`])
      .map(({cap, price}) => {
        return {cap, price, billingFrequency: BillingFrequency.MONTHLY};
      })
      .concat(
        Object.values<any>(planResults[`annual`]).map(({cap, price}) => {
          return {cap, price, billingFrequency: BillingFrequency.ANNUAL};
        }),
      ),
    packages,
  };
}

/**
 * Retrieve pricing curves for the given org id.
 */
export async function getExperimentalPricingCurve(orgInfo?: OrgPlanInfo): Promise<ExperimentalPricingCurve[]> {
  const orgId = orgInfo?.orgId;
  const [planResults, packageResults] = await Promise.all([
    apiClient.get(pricingCurveApiUrl, {skipAccessToken: true}),
    apiClient.get(`${packagesApiUrl}?orgId=${orgId}`, null, {skipAccessToken: true}),
  ]);
  const packages: PricingCurvePackage[] = Object.entries<any>(packageResults)
    .map(marshalPackageInfo)
    .filter(({id}) => id); // make sure package exists in id map just in case, for M&E

  return (planResults[`curves`] as any[]).map((planResult) => {
    return {
      planId: planResult[`plan_id`],
      agreementGroupId: planResult[`agreement_group_id`],
      curveName: planResult[`curve_name`],
      billingFrequency: planResult[`frequency`],
      sliderInfo: planResult[`slider_info`],
      points: convertPriceToFloat(planResult[`points`]),
      packages,
      isEnterprise: planResult[`is_enterprise`],
      excludedQuantityFromOverage: planResult[`excluded_quantity_from_overage`],
      defaultOverageMultiplier: planResult[`default_overage_multiplier`],
    };
  });
}

function convertPriceToFloat(points: {[key: string]: {cap: number; price: string; overage_rate: string}}) {
  const converted = {};

  Object.keys(points).forEach((key) => {
    converted[key] = {...points[key], price: parseFloat(points[key].price), overageRate: points[key].overage_rate};
  });

  return converted;
}

function getCustomerTypeFromInfo(info): CustomerType {
  // Some contract users have 0 arr (ex. enterprise plan); contract status takes precedent over free vs paid.
  if (info.is_on_contract) {
    return info.events_plan.is_mtu ? CustomerType.CONTRACT_MTU : CustomerType.CONTRACT_LEGACY;
  }

  if (info.arr === 0) {
    // Customer is in some free world
    if (info.is_pricing_2023) {
      return CustomerType.FREE_PRICING_2023;
    } else if (!info.events_plan.is_mtu) {
      return CustomerType.FREE_LEGACY;
    } else if (info.events_plan.cap === 1000) {
      return CustomerType.FREE_MTU;
    } else if (info.events_plan.cap === 100000) {
      return CustomerType.FREE_MTU_100K;
    } else {
      // Customer is on an unusual free MTU plan, default to legacy
      return CustomerType.FREE_LEGACY;
    }
  } else {
    if (info.is_pricing_2023) {
      return CustomerType.PREMIUM_PRICING_2023;
    } else if (info.events_plan.is_legacy_selfserve || info.people_plan.is_legacy_selfserve) {
      return CustomerType.SELFSERVE_LEGACY;
    } else if (info.is_v1_mtu_growth || info.is_v2_mtu_growth || info.is_v2_1_mtu_growth || info.is_v2_1_1_mtu_growth) {
      return CustomerType.SELFSERVE_MTU;
    } else if (info.events_plan && info.legacy_plan_descriptions) {
      return CustomerType.GENERIC_LEGACY;
    }
  }
  return CustomerType.NONE;
}

export async function getCustomerInfo(
  {isPricing2023}: {isPricing2023: boolean} = {isPricing2023: false},
): Promise<CustomerInfo> {
  const results: any = await apiClient.get(planInfoApiUrl, isPricing2023 ? {pricing_2023: true} : null, {
    skipAccessToken: true,
  });
  const isAuthenticated = results.is_authenticated;

  // Always use lower pricing curve
  // TODO(Pricing 2023) - don't need this?
  const curveId = PricingCurveId.V2_1_GROWTH;

  // User and org info should only be populated if authenticated
  if (!isAuthenticated) {
    return {
      isAuthenticated,
      curveId,
      userInfo: null,
      orgInfo: null,
    };
  }

  const orgInfo = await getOrgInfo(results);

  return {
    isAuthenticated,
    curveId,
    userInfo: getUserInfo(results.user),
    orgInfo,
  };
}

function getPlanDescription(info) {
  const planDescriptions = info.legacy_plan_descriptions?.lines;
  if (!planDescriptions) {
    return null;
  }

  return planDescriptions.map((line) => ({
    description: line.description,
    price: line.price,
    dateString: line.date_string,
    packages: line.packages,
  }));
}

function getUserInfo(user): UserInfo | null {
  if (user) {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
    };
  }

  return null;
}

async function getOrgInfo(results): Promise<OrgPlanInfo | null> {
  if (!results.orgs || !Object.keys(results.orgs).length) {
    return null;
  }

  // We determine which org the user wishes to administer based on the query string param
  // This param is not present when the user is only able to administer one org, so we use the first org in that case.
  const urlOptions = queryString.parse(window.location.search);
  const organizationId =
    urlOptions.org && Object.prototype.hasOwnProperty.call(results.orgs, urlOptions.org)
      ? urlOptions.org
      : Object.keys(results.orgs)[0];
  const info = results.orgs[organizationId];

  const isOnBumpRes = await getOrgIsOnBump(organizationId);
  const isOnBump = isOnBumpRes[info.account_id];

  let currentCurveId = PricingCurveId.V2_1_1_GROWTH;

  if (info.is_v1_mtu_growth) {
    currentCurveId = PricingCurveId.V1_GROWTH;
  } else if (info.is_v2_mtu_growth) {
    currentCurveId = PricingCurveId.V2_GROWTH;
  } else if (info.is_v2_1_mtu_growth) {
    currentCurveId = PricingCurveId.V2_1_GROWTH;
  } else if (info.is_pricing_2023) {
    currentCurveId = PricingCurveId.PRICING_2023;
  }

  let orgInfo: OrgPlanInfo = {
    orgId: parseInt(organizationId, 10),
    accountId: info.account_id,
    canDowngradeOrTransitionOnline: info.can_downgrade_or_transition_online,
    canPurchaseOnline: info.can_purchase_online,
    hasBillingPermissions: info.has_billing_permissions,
    currentPlanState: {
      customerType: getCustomerTypeFromInfo(info),
      curveId: currentCurveId,
      // TODO (Pricing 2023 Clean-up): this is a hack to enable experiment; API contract shape has changed
      // After the experiment, either merge or migrate all paths of API to return the same schema for response
      cap: info.is_pricing_2023 ? info.events_plan.cap / info.events_plan.term_length : info.mtu_level,
      billingFrequency:
        info.events_plan.term_length === 1
          ? BillingFrequency.MONTHLY
          : info.events_plan.term_length === 12
            ? BillingFrequency.ANNUAL
            : BillingFrequency.CUSTOM,
      packageIds: info.packages.map((oldId) => PACKAGE_ID_BY_NAME[oldId]),
      // Curve variant will be set to enterprise for all enterprise customers regardless of whether they self serve purchased or not
      curveVariant: info.events_plan.is_enterprise ? PricingCurveVariant.ENTERPRISE : PricingCurveVariant.GROWTH,
      isNewStartupProgramCustomer: info.events_plan.is_startup,
    },
    orgName: info.org_name,
    mtuUsage: info.mtu_usage,
    arr: info.arr,
    billingCredits: info.billing_credits.map((credit) => ({
      appliesTo: credit.applies_to,
      endDate: credit.end_date,
      initialValue: parseFloat(credit.initial_value),
      name: credit.name,
      remainingBalance: parseFloat(credit.remaining_balance),
      state: credit.state,
    })),
    usageWasLastMonth: info.usage_was_last_month,
    eventsBillingPeriod:
      info.events_plan.billing_period_start_date && info.events_plan.billing_period_end_date
        ? {
            startDate: new Date(info.events_plan.billing_period_start_date),
            endDate: new Date(info.events_plan.billing_period_end_date),
          }
        : null,
    peopleBillingPeriod:
      info.people_plan.billing_period_start_date && info.people_plan.billing_period_end_date
        ? {
            startDate: new Date(info.people_plan.billing_period_start_date),
            endDate: new Date(info.people_plan.billing_period_end_date),
          }
        : null,
    legacyPlanDescription: getPlanDescription(info),
    isExperimentPlan: info.is_pricing_2023,
    isOnBump,
    isBumpPbEnabled: false, // default to false, potentially set from bump call below
  };

  if (isOnBump) {
    const bumpAccountInfo = await getBumpBillingAccount(info.account_id);
    const packageIds = bumpAccountInfo?.addon_feature_gates.map(
      (featureGateName) => PACKAGE_ID_BY_NAME[featureGateName],
    );
    const newCurrentPlanState = {
      ...orgInfo.currentPlanState,
      packageIds,
      billingFrequency: bumpAccountInfo.current_agreements.some((a) => a.name.includes(`Annual`))
        ? BillingFrequency.ANNUAL
        : BillingFrequency.MONTHLY,
      curveVariant: bumpAccountInfo.is_enterprise ? PricingCurveVariant.ENTERPRISE : PricingCurveVariant.GROWTH,
    };
    orgInfo = {
      ...orgInfo,
      agreements: bumpAccountInfo.current_agreements,
      isPaid: bumpAccountInfo.is_paid,
      currentPlanState: newCurrentPlanState,
      isBumpPbEnabled: bumpAccountInfo.is_bump_pb_enabled,
    };
  }

  return orgInfo;
}

export async function fetchClientSecretAndPaymentMethod(accountId: number) {
  /**
   * Retrieves the client secret, and a payment method if extant, from our backend for the provided account.
   */
  try {
    const response = await handleJsonRequest(
      fetch(`/api/app/billing/create_setup_intent/`, {
        method: `POST`,
        body: JSON.stringify({account_id: accountId}),
      }),
    );
    const results = {clientSecret: response.results.client_secret, paymentMethod: ``};
    if (`payment_method_id` in response.results) {
      results.paymentMethod = response.results.payment_method_id;
    }
    return results;
  } catch {
    throw new Error(`There was an error retrieving client secret and payment method..`);
  }
}

export async function handleCardSetup(clientSecret: string, paymentMethod: any, stripeObj?: any) {
  /**
   * Sets up a payment method specified within a Stripe Elements card element using the stripe.js api.
   * The card element should have been created using https://stripe.com/docs/js/elements_object/create_element?type=card
   * For documentation on stripe.confirmCardSetup, see https://stripe.com/docs/js/setup_intents/confirm_card_setup
   * Our use case specifically is https://stripe.com/docs/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-with_element
   */
  let error: any = null;
  try {
    if (!clientSecret || !paymentMethod) {
      error = new Error(`Client secret and payment method must be populated.`);
    } else {
      const result = await (stripeObj ? stripeObj : stripe).confirmCardSetup(clientSecret, {
        payment_method: typeof paymentMethod === `string` ? paymentMethod : {card: paymentMethod},
      });
      if (result.error && result.error.code) {
        // Stripe API says: errors that could be handled programmatically (e.g., a card is declined)
        // include an error code—a short string with a brief explanation—as a value for code.
        error = result.error.message;
      } else if (result.error) {
        error = result.error;
      } else {
        return result.setupIntent.payment_method.toString();
      }
    }
  } catch {
    error = `Error trying to attach your card details to your account for payment.`;
  }

  if (error !== null) {
    throw new Error(error);
  }
}

// Temp for the new plan builder
// TODO (leondai_mixpanel): remove the other one once old plan builder code is deprecated
// this call can error but still return the setup_intent object which denotes a "success"
export async function handleCardSetupV2(clientSecret: string, paymentMethod: any, stripeObj?: any) {
  /**
   * Sets up a payment method specified within a Stripe Elements card element using the stripe.js api.
   * The card element should have been created using https://stripe.com/docs/js/elements_object/create_element?type=card
   * For documentation on stripe.confirmCardSetup, see https://stripe.com/docs/js/setup_intents/confirm_card_setup
   * Our use case specifically is https://stripe.com/docs/js/setup_intents/confirm_card_setup#stripe_confirm_card_setup-with_element
   */
  let error: any = null;
  try {
    if (!clientSecret || !paymentMethod) {
      error = new Error(`Client secret and payment method must be populated.`);
    } else {
      const result = await (stripeObj ? stripeObj : stripe).confirmCardSetup(clientSecret, {
        payment_method: typeof paymentMethod === `string` ? paymentMethod : {card: paymentMethod},
      });
      if (result.error && result.error.setup_intent) {
        return result.error.setup_intent.payment_method.toString();
      } else if (result.error && result.error.code) {
        error = result.error.message;
      } else if (result.error) {
        error = result.error;
      } else {
        return result.setupIntent.payment_method.toString();
      }
    }
  } catch {
    error = `Error trying to attach your card details to your account for payment.`;
  }
  if (error !== null) {
    throw new Error(error);
  }
}

export async function attachPaymentMethod(
  accountId: number,
  paymentMethodId: string,
  country: string,
  postalCode: string,
): Promise<boolean> {
  try {
    const response = await handleJsonRequest(
      fetch(`/api/app/billing/attach_payment_method/`, {
        method: `POST`,
        body: JSON.stringify({
          account_id: accountId,
          payment_method_id: paymentMethodId,
          country,
          postal_code: postalCode,
        }),
      }),
    );
    return response.results;
  } catch {
    throw new Error(`There was an error attaching the payment method.`);
  }
}

export async function getCardInfo(accountId: number) {
  try {
    const response = await handleJsonRequest(
      fetch(`/api/app/billing/card_info/`, {
        method: `POST`,
        body: JSON.stringify({
          account_id: accountId,
        }),
      }),
    );

    return response.results.cardInfo;
  } catch {
    throw new Error(`There was an error getting card info.`);
  }
}

export async function submitSelfServePurchase(request: PurchaseRequest): Promise<boolean> {
  const {results} = await handleJsonRequest(
    fetch(selfServePurchaseUrl, {
      method: `POST`,
      body: objToFormData(request),
    }),
  );

  return results.success === true;
}

export async function submitSelfServeChange(request: ChangeRequest): Promise<boolean> {
  const {results} = await handleJsonRequest(
    fetch(selfServeChangeUrl, {
      method: `POST`,
      body: objToFormData(request),
    }),
  );

  return results.success === true;
}

export async function submitSelfServeQuoteChange(request: QuoteChangeRequest): Promise<AmountDueInfo> {
  const {results} = await handleJsonRequest(
    fetch(selfServeQuoteChangeUrl, {
      method: `POST`,
      body: objToFormData(request),
    }),
  );

  if (results.success) {
    return {
      amount: Math.max(0, -1 * parseFloat(results.amount_due)),
      creditsApplied: results.credits_applied ? parseFloat(results.credits_applied) : 0,
      currentBalance: parseFloat(results.current_balance),
      lines: results.breakdown_lines.map((line) => {
        return {
          amount: line.amount,
          description: line.short_description,
          state: line.state,
        };
      }),
      startDate: results.start_date,
      renewalDate: results.renewal_date,
      is_annual_downgrade: results.is_annual_downgrade,
      is_bump_purchase: results.is_bump_purchase || false,
    };
  } else {
    return {
      amount: null,
    };
  }
}

export async function getOwnerAccount(orgId) {
  try {
    const {results} = await handleJsonRequest(
      fetch(`/organizations/${orgId}/users`, {
        method: `GET`,
      }),
    );

    return results
      .filter((user) => user.org_role === `owner`) // only owners
      .reduce((a, b) => (a.last_activity > b.last_activity ? a : b)); // latest active
  } catch {
    throw new Error(`Error retrieving users`);
  }
}

// TODO (Pricing 2023 Cleanup): merge with method above
export async function getOwnerAccounts(
  orgId,
  {count}: {count: number} = {count: 1},
): Promise<Array<{[key: string]: any}>> {
  try {
    const {results} = await handleJsonRequest(
      fetch(`/organizations/${orgId}/users`, {
        method: `GET`,
      }),
    );

    return take(
      results
        .filter((user) => user.org_role === `owner`) // only owners
        .sort((a, b) => b.last_activity - a.last_activity) /* sort by latest active */,
      count,
    );
  } catch {
    throw new Error(`Error retrieving users`);
  }
}

export async function getPlanRecommendation(accountId: number) {
  try {
    const url = `/api/app/billing/recommend_plan?account_id=${accountId}`;
    const {results} = await handleJsonRequest(
      fetch(url, {
        method: `GET`,
      }),
    );
    if (!results.success) {
      throw new Error(`Error retrieving plan recommendation`);
    }
    return results;
  } catch (err) {
    console.error(err);
  }
}

export async function getExperimentPlanRecommendation(accountId: number) {
  try {
    const url = `/api/app/billing/recommend_experiment_plan?account_id=${accountId}`;
    const {results, status} = await handleJsonRequest(
      fetch(url, {
        method: `GET`,
      }),
    );
    if (status !== `ok`) {
      throw new Error(`Error retrieving plan recommendation`);
    }
    return results;
  } catch (err) {
    console.error(err);
  }
}

export async function getOrgIsOnBump(orgId) {
  try {
    const url = `/api/app/billing_v2/is_on_bump/organization/${orgId}`;
    const {results, status} = await handleJsonRequest(
      fetch(url, {
        method: `GET`,
      }),
    );
    if (status !== `ok`) {
      throw new Error(`Error retrieving BUMP status`);
    }
    return results;
  } catch (err) {
    addToast({
      type: ToastType.ERROR,
      isError: true,
      message: `Error loading Plan Builder`,
    });
    console.error(err);
  }
}

export async function getBumpBillingAccount(accountId): Promise<BumpBillingData> {
  try {
    const url = `/api/app/billing_v2/get_billing_info/account/${accountId}`;
    const {results, status} = await handleJsonRequest(
      fetch(url, {
        method: `GET`,
      }),
    );
    if (status !== `ok`) {
      throw new Error(`Error retrieving BUMP account details`);
    }
    return results;
  } catch (err) {
    addToast({
      type: ToastType.ERROR,
      isError: true,
      message: `Error loading Plan Builder`,
    });
    console.error(err);
    return null;
  }
}
